import type { TFunction } from 'i18next';
import { api } from '@/lib/api';
import { managedHelpTopicClientId } from './help-managed-topic-id';
import type { HelpCategory } from './help-center-topics';
import { resolveHelpCenterIcon } from './resolve-help-center-icon';
import { resolveHelpText } from './help-topic-i18n';
import { resolveStoredModalDetail } from './help-topic-detail-i18n';
import { matchStaticHelpCategoryId, matchStaticHelpTopicId } from './match-static-help-id';

export type HelpCenterManagedTopicDto = {
  id: string;
  categoryId: string;
  title: string;
  description: string;
  titleI18n?: Record<string, string> | null;
  descriptionI18n?: Record<string, string> | null;
  detailI18n?: Record<string, unknown> | null;
  formationUrl: string | null;
  legacyTopicId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type HelpCenterManagedCategoryDto = {
  id: string;
  title: string;
  description: string;
  iconKey: string;
  sortOrder: number;
  isActive: boolean;
  legacyCategoryId: string | null;
  createdAt: string;
  updatedAt: string;
  topics: HelpCenterManagedTopicDto[];
};

export async function fetchPublicManagedHelpCategories(): Promise<HelpCenterManagedCategoryDto[]> {
  try {
    const res = await api.get<{ success: boolean; data: { categories: HelpCenterManagedCategoryDto[] } }>(
      '/public/help-center/managed',
    );
    return res.data?.categories ?? [];
  } catch {
    return [];
  }
}

/** Carte topicId UI → URL formation (pour le lanceur). */
export function buildManagedFormationUrlMap(
  categories: HelpCenterManagedCategoryDto[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const c of categories) {
    for (const t of c.topics) {
      const url = (t.formationUrl ?? '').trim();
      if (!url) continue;
      const clientId = t.legacyTopicId ?? managedHelpTopicClientId(t.id);
      map[clientId] = url;
    }
  }
  return map;
}

/**
 * Convertit l’API publique en rubriques au format centre d’aide.
 * Si legacyTopicId / legacyCategoryId sont absents en base, on ré-apparie sur le titre FR
 * du catalogue statique pour brancher HELP_TOPIC_DETAILS et les clés help.topics.*.
 */
export function helpCenterApiRowsToCategories(
  categories: HelpCenterManagedCategoryDto[],
  lang: string,
  t?: TFunction,
): HelpCategory[] {
  return categories.map((c) => {
    const categoryId =
      (c.legacyCategoryId ?? '').trim() ||
      matchStaticHelpCategoryId(c.title) ||
      `managed-cat:${c.id}`;
    const categoryTitle = t
      ? t(`help.categories.${categoryId}.title`, c.title)
      : c.title;
    const categoryDescription = t
      ? t(`help.categories.${categoryId}.description`, c.description)
      : c.description;
    return {
      id: categoryId,
      title: categoryTitle,
      description: categoryDescription,
      icon: resolveHelpCenterIcon(c.iconKey),
      topics: c.topics.map((topic) => {
        const id =
          (topic.legacyTopicId ?? '').trim() ||
          matchStaticHelpTopicId(topic.title, topic.titleI18n) ||
          managedHelpTopicClientId(topic.id);
        const fromI18nTitle = resolveHelpText(topic.titleI18n, lang, '');
        const fromI18nDesc = resolveHelpText(topic.descriptionI18n, lang, '');
        const title =
          fromI18nTitle ||
          (t ? t(`help.topics.${id}.title`, topic.title) : topic.title);
        const description =
          fromI18nDesc ||
          (t ? t(`help.topics.${id}.description`, topic.description) : topic.description);
        // detailI18n vide ({}) ne doit pas masquer le contenu bundlé : null → base locale.
        const storedDetail = resolveStoredModalDetail(topic.detailI18n, lang);
        return {
          id,
          title,
          description,
          detail: storedDetail,
        };
      }),
    };
  });
}

